"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolvePortraitId = resolvePortraitId;
exports.createReadOnlyScriptContext = createReadOnlyScriptContext;
const InventoryManager_1 = require("../models/InventoryManager");
const dateTime_1 = require("../utils/dateTime");
const VariableStore_1 = require("./VariableStore");
const sessionPlayerDisplayName_1 = require("./sessionPlayerDisplayName");
const TimerState_1 = require("./TimerState");
const openingSessionStack_1 = require("./openingSessionStack");
const activityScriptHelpers_1 = require("../activities/activityScriptHelpers");
const entityScriptHelpers_1 = require("../entities/entityScriptHelpers");
const foreignEntityScriptHelpers_1 = require("../entities/foreignEntityScriptHelpers");
const characterInstanceNames_1 = require("../utils/characterInstanceNames");
const openCharacterRuntime_1 = require("../utils/openCharacterRuntime");
const characterScriptContext_1 = require("./characterScriptContext");
const scheduleEventHelpers_1 = require("./scheduleEventHelpers");
const adventureTags_1 = require("../utils/adventureTags");
const scriptApiExtensions_1 = require("./scriptApiExtensions");
const hostScriptBagHelpers_1 = require("./hostScriptBagHelpers");
const characterMap_1 = require("./characterMap");
/**
 * Resolves portrait IDs that may contain ${variable} interpolations.
 * Uses variableStore.getVariable for substitution.
 */
function resolvePortraitId(rawPortraitId, characterName, targetPlayer, variableStore, gameSession) {
    const basePortrait = (rawPortraitId ?? 'generic').trim();
    if (!basePortrait) {
        return 'generic';
    }
    if (!basePortrait.includes('${')) {
        return basePortrait;
    }
    if (!variableStore) {
        return 'generic';
    }
    let playerForContext = targetPlayer ?? null;
    if (!playerForContext || playerForContext.characterName !== characterName) {
        const resolvedPlayer = gameSession.getPlayerByCharacter(characterName);
        if (resolvedPlayer) {
            playerForContext = resolvedPlayer;
        }
    }
    if (!playerForContext) {
        return 'generic';
    }
    const playerUserId = playerForContext.userId || 'anonymous';
    const variableContext = VariableStore_1.VariableStore.buildVariableContext(playerForContext.id, playerForContext.characterName, playerForContext.currentScene, playerUserId);
    const resolved = basePortrait.replace(/\$\{([^}]+)\}/g, (_, variableName) => {
        const trimmed = variableName.trim();
        const value = variableStore.getVariable(trimmed, variableContext);
        return value === undefined || value === null ? '' : String(value);
    });
    const trimmedResolved = resolved.trim();
    return trimmedResolved.length > 0 ? trimmedResolved : 'generic';
}
/**
 * Create read-only script context with shared helper functions.
 * Used by both InterpolationPipeline (script blocks) and ScriptEvaluator (conditions).
 */
function createReadOnlyScriptContext(params) {
    const resolveNowMs = () => (params.getNowMs ? params.getNowMs() : Date.now());
    const { player, gameSession, variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, analyticsService, evaluateConditionScript, executeConditionForPlayer, entitiesService, foreignEntityLookup } = params;
    const inventoryManager = new InventoryManager_1.InventoryManager(gameSession);
    const getScheduleData = (scheduleVar) => {
        if (!variableStore || !scheduleVar || typeof scheduleVar !== 'string') {
            return { events: [] };
        }
        const context = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
        const raw = variableStore.getVariable(scheduleVar, context);
        if (raw === undefined || raw === null || raw === '') {
            return { events: [] };
        }
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            const events = Array.isArray(parsed?.events) ? parsed.events : [];
            return { events };
        }
        catch {
            return { events: [] };
        }
    };
    const getVar = (name) => {
        if (entitiesService) {
            const cur = entitiesService.getCurrentEntityId(player.id);
            if (cur) {
                const ev = entitiesService.getEntityVariable(cur, name);
                if (ev !== undefined) {
                    return (0, characterMap_1.unwrapEntityVariableValue)(ev, player.characterName);
                }
            }
        }
        if (!variableStore)
            return undefined;
        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
        return variableStore.getVariable(name, ctx);
    };
    /** Prefer non-empty user selection; fall back to adventure/player schedule_event_id. */
    const resolveSelectedScheduleEventId = (eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
        const fromUser = getVar(userSelectedVar);
        if (fromUser !== undefined && fromUser !== null && String(fromUser).trim() !== '') {
            return String(fromUser).trim();
        }
        const fromEventVar = getVar(eventIdVar);
        if (fromEventVar !== undefined && fromEventVar !== null && String(fromEventVar).trim() !== '') {
            return String(fromEventVar).trim();
        }
        return '';
    };
    const evaluateConditionForCharacter = executeConditionForPlayer
        ? (0, characterScriptContext_1.createEvaluateConditionForCharacter)(gameSession, player, executeConditionForPlayer)
        : (characterName, script) => {
            const trimmed = (script ?? '').trim();
            if (!trimmed) {
                return true;
            }
            const char = (characterName ?? '').trim();
            if (!char || char !== player.characterName || !evaluateConditionScript) {
                return false;
            }
            return evaluateConditionScript(trimmed);
        };
    const characterScriptAccess = {
        getVariableForCharacter: (characterName, variableName) => {
            if (!variableStore) {
                return undefined;
            }
            return (0, characterScriptContext_1.readVariableForCharacter)(variableStore, gameSession, player, characterName, variableName);
        },
        evaluateConditionForCharacter
    };
    const escapeAttr = (value) => String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const clampAnalyticsLimit = (limit) => Math.max(1, Math.min(100, Math.floor(Number(limit)) || 10));
    const toAdventureMarkupId = (raw) => {
        const s = (raw ?? '').trim().toLowerCase();
        // Preserve path adventure IDs (e.g. events/lapa-party/host) for <adventure:…> lookup.
        if (/^[a-z0-9]+(?:[/-][a-z0-9]+)*$/.test(s)) {
            return s;
        }
        // Legacy / dirty analytics ids: coerce to a hyphenated slug.
        const cleaned = s.replace(/_/g, '-').replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
        return cleaned.length > 0 ? cleaned : 'generic';
    };
    const escapeJsDoubleQuotedString = (s) => String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const resolveSessionPlayerDisplayName = (sessionPlayer) => (0, sessionPlayerDisplayName_1.resolveSessionPlayerDisplayName)(sessionPlayer, {
        variableStore,
        gameSession,
        contextPlayer: player
    });
    const feedbackRowToCardMarkup = (row, selectionTarget, includeContextDetails = true) => {
        const feedbackId = escapeAttr(row.id || '');
        const feedbackType = escapeAttr(row.feedbackType || 'Comment');
        const title = escapeAttr(row.title || '');
        const displayName = escapeAttr(row.displayName || 'Unknown');
        const occurredAt = escapeAttr(row.occurredAt || '');
        const contentPreview = escapeAttr(row.contentPreview || '');
        const targetAttr = typeof selectionTarget === 'string' && selectionTarget.trim().length > 0
            ? ` selectionTarget="${escapeAttr(selectionTarget.trim())}"`
            : '';
        let contextAttrs = '';
        if (includeContextDetails) {
            const scene = (row.sceneId ?? '').trim();
            const deployment = (row.appDomain ?? '').trim();
            const advName = (row.adventureName ?? '').trim();
            const advId = (row.adventureId ?? '').trim();
            const advDisplay = advName || advId;
            contextAttrs = [
                scene ? ` scene="${escapeAttr(scene)}"` : '',
                deployment ? ` deployment="${escapeAttr(deployment)}"` : '',
                advDisplay ? ` adventure="${escapeAttr(advDisplay)}"` : '',
            ].join('');
        }
        return `<FeedbackCard id="${feedbackId}" type="${feedbackType}" title="${title}" by="${displayName}" at="${occurredAt}" preview="${contentPreview}"${contextAttrs}${targetAttr}>`;
    };
    /** Format epoch seconds as a friendly local date/time for the viewing player. */
    const formatFriendlyDateTimeForPlayer = (epochSeconds) => (0, dateTime_1.formatFriendlyDateTimeFromOffset)(epochSeconds, getTimezoneOffsetMinutes(), resolveNowMs());
    /** Get user's timezone offset (minutes east of UTC) from variable or player, for local time display. */
    const getTimezoneOffsetMinutes = () => {
        const fromPlayer = player.timeZoneOffsetMinutes;
        if (fromPlayer !== undefined && fromPlayer !== null && Number.isFinite(Number(fromPlayer))) {
            return Number(fromPlayer);
        }
        if (!variableStore)
            return 0;
        const ctx = VariableStore_1.VariableStore.buildVariableContext(player.id, player.characterName, player.currentScene, player.userId || 'anonymous');
        const raw = variableStore.getVariable('user_time_zone_offset_minutes', ctx);
        if (raw === undefined || raw === null)
            return 0;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : 0;
    };
    const resolvePortraitForCharacterName = (name) => {
        if (!adventureManager)
            return 'generic';
        const adventure = adventureManager.getAdventure(gameSession.adventureId);
        if (!adventure)
            return 'generic';
        const character = adventure.getCharacter(name);
        if (!character)
            return 'generic';
        const targetPlayer = gameSession.getPlayerByCharacter(name);
        if (targetPlayer) {
            return resolvePortraitId(character.portraitId, name, targetPlayer, variableStore, gameSession);
        }
        const rawPortraitId = (character.portraitId || '').trim();
        if (!variableStore) {
            if (!rawPortraitId)
                return 'generic';
            return rawPortraitId.includes('${') ? 'generic' : rawPortraitId;
        }
        // If character has a static portrait defined (no ${}), always use it
        if (rawPortraitId && !rawPortraitId.includes('${')) {
            return rawPortraitId;
        }
        // Only use character_last_user_id when portrait is dynamic (${user_portrait_id})
        const characterContext = VariableStore_1.VariableStore.buildVariableContext(player.id, name, player.currentScene, player.userId || 'anonymous');
        const lastUserId = variableStore.getVariable('character_last_user_id', characterContext);
        if (lastUserId) {
            const userContext = VariableStore_1.VariableStore.buildVariableContext(player.id, name, player.currentScene, String(lastUserId));
            const userPortrait = variableStore.getVariable('user_portrait_id', userContext);
            if (typeof userPortrait === 'string' && userPortrait.trim().length > 0) {
                return userPortrait;
            }
        }
        if (!rawPortraitId)
            return 'generic';
        if (rawPortraitId.includes('${')) {
            return 'generic';
        }
        return rawPortraitId;
    };
    const doGenerateScheduleEventTimeOptions = (scheduleVar, eventId, selectionTarget) => {
        if (!scheduleVar || !eventId) {
            return 'No event selected.';
        }
        const { events } = getScheduleData(scheduleVar);
        const event = events.find((e) => e?.id === eventId);
        if (!event) {
            return 'Event not found.';
        }
        const scheduledStart = Number(event.scheduledTimeEpochSeconds);
        if (Number.isFinite(scheduledStart) && scheduledStart > 0)
            return '';
        const times = Array.isArray(event.proposedTimes) ? event.proposedTimes : [];
        if (times.length === 0) {
            return 'No proposed times yet.';
        }
        const escapeAttr = (value) => String(value)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        const targetAttr = selectionTarget ? ` selectionTarget="${selectionTarget}"` : '';
        const allPortraits = Array.isArray(event.requiredCharacters)
            ? event.requiredCharacters.map(resolvePortraitForCharacterName).filter(Boolean)
            : [];
        const allPortraitsAttr = allPortraits.length > 0 ? ` requiredPortraits="${escapeAttr(allPortraits.join(','))}"` : '';
        const options = times
            .map((p) => ({
            start: Number(p.startTimeEpochSeconds),
            acceptedBy: Array.isArray(p.acceptedBy) ? p.acceptedBy.map(String) : [],
            realWorldLocation: typeof p.realWorldLocation === 'string' ? p.realWorldLocation.trim() : ''
        }))
            .filter((entry) => Number.isFinite(entry.start) && entry.start > 0)
            .map((entry) => {
            const acceptedPortraits = entry.acceptedBy.map(resolvePortraitForCharacterName).filter(Boolean);
            const acceptedAttr = acceptedPortraits.length > 0 ? ` accepted="${escapeAttr(acceptedPortraits.join(','))}"` : '';
            const locationAttr = entry.realWorldLocation.length > 0
                ? ` realWorldLocation="${escapeAttr(entry.realWorldLocation)}"`
                : '';
            return `<ScheduleEventTime eventId="${eventId}" start="${entry.start}"${targetAttr}${allPortraitsAttr}${acceptedAttr}${locationAttr}>`;
        });
        if (options.length === 0)
            return 'No proposed times yet.';
        return options.join('\n');
    };
    const getTimerState = (timerVar) => {
        if (!timerVar || typeof timerVar !== 'string')
            return null;
        const raw = getVar(timerVar);
        if (raw === undefined || raw === null)
            return null;
        return (0, TimerState_1.parseTimerState)(typeof raw === 'string' ? raw : String(raw));
    };
    const timerDisplayTypeFromMode = (mode) => {
        if (mode === 'max')
            return 'countdown';
        if (mode === 'deadline')
            return 'deadline-countdown';
        return mode;
    };
    const escapeTimerAttr = (value) => String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const appendPreCountdownAttrs = (state, attrs) => {
        if (state.armedAt !== null)
            attrs.push(`armedAt="${state.armedAt}"`);
        if (state.preCountdownSeconds !== null) {
            attrs.push(`preCountdownSeconds="${state.preCountdownSeconds}"`);
        }
        if (state.preCountdownAudioId !== null) {
            attrs.push(`preCountdownAudioId="${escapeTimerAttr(state.preCountdownAudioId)}"`);
        }
    };
    return {
        // Player queries
        hasItem: (itemTag) => player.hasItem(itemTag),
        hasStat: (statName) => player.hasStat(statName),
        hasVisited: (sceneId) => player.hasVisited(sceneId),
        isPlayer: (characterName) => player.characterName === characterName,
        // Other player queries
        otherHasItem: (characterName, itemTag) => {
            const otherPlayer = gameSession.getPlayerByCharacter(characterName);
            return otherPlayer ? otherPlayer.hasItem(itemTag) : false;
        },
        otherHasStat: (characterName, statName) => {
            const otherPlayer = gameSession.getPlayerByCharacter(characterName);
            return otherPlayer ? otherPlayer.hasStat(statName) : false;
        },
        otherHasVisited: (characterName, sceneId) => {
            const otherPlayer = gameSession.getPlayerByCharacter(characterName);
            return otherPlayer ? otherPlayer.hasVisited(sceneId) : false;
        },
        // Unified inventory functions
        hasItemInInventory: (ownerId, ownerType, itemTag) => {
            return inventoryManager.hasItem(ownerId, ownerType, itemTag);
        },
        locationHasItem: (locationTag, itemTag) => {
            return inventoryManager.hasLocationItem(locationTag, itemTag);
        },
        // Helper functions for description scripting
        getItemName: (itemTag) => {
            if (!adventureManager) {
                console.warn(`getItemName called for '${itemTag}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getItemName called but adventure not found`);
                return '';
            }
            const item = adventure.getItem(itemTag);
            if (!item) {
                throw new Error(`Item '${itemTag}' not found in adventure`);
            }
            return item.name;
        },
        getLocationName: (locationTag) => {
            if (!adventureManager) {
                console.warn(`getLocationName called for '${locationTag}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getLocationName called but adventure not found`);
                return '';
            }
            const location = adventure.getLocation(locationTag);
            if (!location) {
                throw new Error(`Location '${locationTag}' not found in adventure`);
            }
            return location.name;
        },
        getItemIcon: (itemTag) => {
            if (!adventureManager) {
                console.warn(`getItemIcon called for '${itemTag}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getItemIcon called but adventure not found`);
                return '';
            }
            const item = adventure.getItem(itemTag);
            if (!item) {
                throw new Error(`Item '${itemTag}' not found in adventure`);
            }
            return item.iconId;
        },
        getItemDescription: (itemTag) => {
            if (!adventureManager) {
                console.warn(`getItemDescription called for '${itemTag}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getItemDescription called but adventure not found`);
                return '';
            }
            const item = adventure.getItem(itemTag);
            if (!item) {
                throw new Error(`Item '${itemTag}' not found in adventure`);
            }
            return item.description;
        },
        // Timer read API
        getTimerElapsedSeconds: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return 0;
            return (0, TimerState_1.getElapsedSeconds)(state, resolveNowMs());
        },
        getTimerRemainingSeconds: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return null;
            return (0, TimerState_1.getRemainingSeconds)(state, resolveNowMs());
        },
        hasTimerReachedMax: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return false;
            return (0, TimerState_1.hasReachedMax)(state, resolveNowMs());
        },
        getTimerDeadlineRemainingSeconds: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return 0;
            const remaining = (0, TimerState_1.getDeadlineRemainingSeconds)(state, resolveNowMs());
            return remaining === null ? 0 : remaining;
        },
        hasTimerReachedDeadline: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return false;
            return (0, TimerState_1.hasReachedDeadline)(state, resolveNowMs());
        },
        isTimerInPreCountdown: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return false;
            return (0, TimerState_1.isTimerInPreCountdown)(state, resolveNowMs());
        },
        getPreCountdownRemainingSeconds: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state)
                return 0;
            return (0, TimerState_1.getPreCountdownRemainingSeconds)(state, resolveNowMs());
        },
        getTimerLastStoppedElapsedSeconds: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state || state.stoppedAt === null)
                return 0;
            return (0, TimerState_1.getElapsedSeconds)(state, state.stoppedAt);
        },
        /** Format seconds as a friendly duration (e.g. "45 seconds", "9 minutes and 19 seconds"). */
        formatTimerDuration: (totalSeconds) => {
            const n = Math.floor(Number(totalSeconds));
            if (!Number.isFinite(n) || n < 0)
                return '0 seconds';
            if (n === 0)
                return '0 seconds';
            if (n === 1)
                return '1 second';
            if (n < 60)
                return `${n} seconds`;
            const sec = n % 60;
            const min = Math.floor(n / 60) % 60;
            const hr = Math.floor(n / 3600);
            const parts = [];
            if (hr > 0)
                parts.push(hr === 1 ? '1 hour' : `${hr} hours`);
            if (min > 0)
                parts.push(min === 1 ? '1 minute' : `${min} minutes`);
            if (sec > 0)
                parts.push(sec === 1 ? '1 second' : `${sec} seconds`);
            if (parts.length === 1)
                return parts[0];
            if (parts.length === 2)
                return `${parts[0]} and ${parts[1]}`;
            return `${parts[0]}, ${parts[1]} and ${parts[2]}`;
        },
        displayTimer: (timerVar, timerDisplayType) => {
            const state = getTimerState(timerVar);
            if (!state || !timerVar)
                return '';
            const type = timerDisplayType && ['blind', 'stopwatch', 'countdown', 'min', 'deadline-countdown'].includes(timerDisplayType)
                ? timerDisplayType
                : timerDisplayTypeFromMode(state.mode);
            const safeVar = escapeTimerAttr(timerVar);
            const safeType = escapeTimerAttr(type);
            const attrs = [`timerVar="${safeVar}"`, `type="${safeType}"`];
            if (state.startedAt !== null)
                attrs.push(`startedAt="${state.startedAt}"`);
            if (state.stoppedAt !== null)
                attrs.push(`stoppedAt="${state.stoppedAt}"`);
            if (state.maxSeconds !== null)
                attrs.push(`maxSeconds="${state.maxSeconds}"`);
            if (state.targetAt !== null)
                attrs.push(`targetAt="${state.targetAt}"`);
            appendPreCountdownAttrs(state, attrs);
            return `<TimerDisplay ${attrs.join(' ')} />`;
        },
        getTimerSoundCues: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state || !timerVar)
                return '[]';
            return JSON.stringify(state.soundCues);
        },
        displayTimerAudio: (timerVar) => {
            const state = getTimerState(timerVar);
            if (!state || !timerVar)
                return '';
            const safeVar = escapeTimerAttr(timerVar);
            const attrs = [`timerVar="${safeVar}"`];
            if (state.startedAt !== null)
                attrs.push(`startedAt="${state.startedAt}"`);
            if (state.stoppedAt !== null)
                attrs.push(`stoppedAt="${state.stoppedAt}"`);
            if (state.maxSeconds !== null)
                attrs.push(`maxSeconds="${state.maxSeconds}"`);
            if (state.targetAt !== null)
                attrs.push(`targetAt="${state.targetAt}"`);
            if (state.soundCues.length > 0) {
                attrs.push(`soundCues="${escapeTimerAttr(JSON.stringify(state.soundCues))}"`);
            }
            appendPreCountdownAttrs(state, attrs);
            return `<TimerAudio ${attrs.join(' ')} />`;
        },
        displayCode: (codeVar) => {
            if (!codeVar || typeof codeVar !== 'string' || !codeVar.trim()) {
                return '';
            }
            const safeVar = escapeTimerAttr(codeVar.trim());
            return `<Code-Display variable="${safeVar}" />`;
        },
        // User info functions - cache-first for other users' variables
        getUserName: (userId) => {
            if (!variableStore) {
                console.warn(`getUserName called for '${userId}' but no VariableStore available`);
                return undefined;
            }
            if (friendsService) {
                const user = friendsService.getUser(userId);
                if (!user) {
                    // User not in friendsService, but continue to try cache anyway
                }
            }
            const cacheService = variableStore.cacheService;
            if (cacheService?.cache) {
                const sessionCache = cacheService.cache.get(gameSession.id);
                if (sessionCache?.userVariablesByUserId) {
                    const userVars = sessionCache.userVariablesByUserId.get(userId);
                    if (userVars) {
                        const userVar = userVars.get('user_player_name');
                        if (userVar?.value !== undefined && userVar?.value !== null) {
                            return String(userVar.value);
                        }
                    }
                }
            }
            const userContext = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId
            };
            const value = variableStore.getVariable('user_player_name', userContext);
            return value !== undefined && value !== null ? String(value) : undefined;
        },
        getUserPortrait: (userId) => {
            if (!variableStore) {
                console.warn(`getUserPortrait called for '${userId}' but no VariableStore available`);
                return undefined;
            }
            if (friendsService) {
                const user = friendsService.getUser(userId);
                if (!user) {
                    // User not in friendsService, but continue to try cache anyway
                }
            }
            const cacheService = variableStore.cacheService;
            if (cacheService?.cache) {
                const sessionCache = cacheService.cache.get(gameSession.id);
                if (sessionCache?.userVariablesByUserId) {
                    const userVars = sessionCache.userVariablesByUserId.get(userId);
                    if (userVars) {
                        const userVar = userVars.get('user_portrait_id');
                        if (userVar?.value !== undefined && userVar?.value !== null) {
                            return String(userVar.value);
                        }
                    }
                }
            }
            const userContext = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId
            };
            const value = variableStore.getVariable('user_portrait_id', userContext);
            return value !== undefined && value !== null ? String(value) : undefined;
        },
        getUserVariable: (userId, variableName) => {
            if (!variableStore) {
                return undefined;
            }
            const userContext = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId
            };
            const value = variableStore.getVariable(variableName, userContext);
            return value !== undefined && value !== null ? value : undefined;
        },
        /** Returns the display name of the other player in the session (e.g. in 2-player games), or fallback when no partner has joined. */
        getPartnerName: (fallback) => {
            const otherPlayer = gameSession.players.find(p => p.id !== player.id);
            if (!otherPlayer?.userId) {
                return fallback ?? 'your partner';
            }
            const name = variableStore
                ? (() => {
                    const userContext = {
                        playerId: player.id,
                        characterName: player.characterName,
                        currentSceneId: player.currentScene,
                        userId: otherPlayer.userId
                    };
                    const value = variableStore.getVariable('user_player_name', userContext);
                    return value !== undefined && value !== null ? String(value) : undefined;
                })()
                : undefined;
            return (name && name.trim()) || (fallback ?? 'your partner');
        },
        /** Returns the pronoun for the other player (they/he/she etc), or fallback when no partner has joined. */
        getPartnerPronoun: (fallback) => {
            const otherPlayer = gameSession.players.find(p => p.id !== player.id);
            if (!otherPlayer?.userId || !variableStore) {
                return fallback ?? 'they';
            }
            const userContext = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: otherPlayer.userId
            };
            const pronoun = variableStore.getVariable('user_pronoun', userContext);
            if (pronoun !== undefined && pronoun !== null && String(pronoun).trim()) {
                return String(pronoun).trim();
            }
            const sex = variableStore.getVariable('user_sex', userContext);
            const sexStr = sex !== undefined && sex !== null ? String(sex).toLowerCase() : '';
            if (sexStr === 'male')
                return 'he';
            if (sexStr === 'female')
                return 'she';
            return fallback ?? 'they';
        },
        getFriendRequest: (requestId) => {
            if (!friendsService) {
                return undefined;
            }
            return friendsService.getFriendRequest(requestId);
        },
        getCharacterName: (characterName) => {
            if (!adventureManager) {
                console.warn(`getCharacterName called for '${characterName}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getCharacterName called but adventure not found`);
                return '';
            }
            const character = adventure.getCharacter(characterName);
            if (!character) {
                throw new Error(`Character '${characterName}' not found in adventure`);
            }
            return character.name;
        },
        getCharacterDescription: (characterName) => {
            if (!adventureManager) {
                console.warn(`getCharacterDescription called for '${characterName}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getCharacterDescription called but adventure not found`);
                return '';
            }
            const character = adventure.getCharacter(characterName);
            if (!character) {
                throw new Error(`Character '${characterName}' not found in adventure`);
            }
            return character.description;
        },
        getCharacterPortrait: (characterName) => {
            if (!adventureManager) {
                console.warn(`getCharacterPortrait called for '${characterName}' but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getCharacterPortrait called but adventure not found`);
                return '';
            }
            const character = adventure.getCharacter(characterName);
            if (!character) {
                throw new Error(`Character '${characterName}' not found in adventure`);
            }
            const targetPlayer = player.characterName === characterName
                ? player
                : gameSession.getPlayerByCharacter(characterName) ?? player;
            return resolvePortraitId(character.portraitId, characterName, targetPlayer, variableStore, gameSession);
        },
        getCurrentAdventureTitleImage: () => {
            if (!adventureManager) {
                console.warn(`getCurrentAdventureTitleImage called but no AdventureManager available`);
                return 'generic';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getCurrentAdventureTitleImage called but adventure not found`);
                return 'generic';
            }
            const header = adventure.getHeader();
            return header.titleImageId ?? 'generic';
        },
        /** Human-readable adventure title from the loaded adventure header (falls back to engine id). */
        getCurrentAdventureTitle: () => {
            if (!adventureManager) {
                console.warn(`getCurrentAdventureTitle called but no AdventureManager available`);
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                console.warn(`getCurrentAdventureTitle called but adventure not found`);
                return '';
            }
            const title = (adventure.title ?? '').toString().trim();
            return title.length > 0 ? title : gameSession.adventureId;
        },
        /**
         * Title image id for the adventure encoded on the top of `opening_session_id`
         * (`triggeringAdventureId` in the frame). Used for RETURN_TO_ADVENTURE UI (e.g. hub button).
         * Returns a namespaced id (`adventureId:titleImageId`) so the client resolves against the
         * source adventure, not the current session. Does not consult `isOpeningSessionValid`.
         */
        getOpeningSessionReturnTitleImage: () => {
            if (!variableStore || !adventureManager) {
                return 'generic';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const top = variableStore.peekStack(openingSessionStack_1.OPENING_SESSION_STACK_VAR, context);
            const parsed = (0, openingSessionStack_1.parseOpeningSessionFrame)(top);
            if (!parsed?.triggeringAdventureId) {
                return 'generic';
            }
            const targetAdventure = adventureManager.getAdventure(parsed.triggeringAdventureId);
            if (!targetAdventure) {
                return 'generic';
            }
            const titleImageId = targetAdventure.getHeader().titleImageId ?? 'generic';
            if (!titleImageId || titleImageId === 'generic') {
                return 'generic';
            }
            // Namespace with the source adventure so the client does not resolve against the current session.
            if (titleImageId.includes(':')) {
                return titleImageId;
            }
            return `${parsed.triggeringAdventureId}:${titleImageId}`;
        },
        // Player state methods
        getCurrentScene: () => player.currentScene,
        getPlayerCharacter: () => player.characterName,
        getCurrentLocation: () => {
            if (!variableStore) {
                return '';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const value = variableStore.getVariable('character_location', context);
            return typeof value === 'string' ? value : '';
        },
        // Read-only state
        inventory: Object.freeze([...player.inventory]),
        stats: Object.freeze([...player.stats]),
        sceneHistory: Object.freeze([...player.sceneHistory]),
        // Variable API
        getVariable: (name) => {
            if (entitiesService) {
                const cur = entitiesService.getCurrentEntityId(player.id);
                if (cur) {
                    const ev = entitiesService.getEntityVariable(cur, name);
                    if (ev !== undefined) {
                        return (0, characterMap_1.unwrapEntityVariableValue)(ev, player.characterName);
                    }
                }
            }
            if (!variableStore) {
                console.warn(`getVariable called for '${name}' but no VariableStore available`);
                return undefined;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            return variableStore.getVariable(name, context);
        },
        /** Read a variable in another character's scope (character, player, scene, and user precedence). */
        getVariableForCharacter: (characterName, variableName) => {
            if (!variableStore) {
                console.warn(`getVariableForCharacter called for '${variableName}' but no VariableStore available`);
                return undefined;
            }
            if (!characterName || !variableName) {
                return undefined;
            }
            return (0, characterScriptContext_1.readVariableForCharacter)(variableStore, gameSession, player, characterName, variableName);
        },
        /**
         * Evaluate a condition script as if the given character were the scripting player
         * (e.g. {@code getPlayerCharacter()} and {@code getVariable()} inside the script).
         */
        evaluateConditionForCharacter,
        /** Character name of the other player in the session, or empty when solo. */
        getPartnerCharacter: () => (0, characterScriptContext_1.getPartnerCharacterName)(gameSession, player),
        peekStack: (name) => {
            if (!variableStore) {
                console.warn(`peekStack called for '${name}' but no VariableStore available`);
                return undefined;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            return variableStore.peekStack(name, context);
        },
        getCharacterMapValue: (name) => {
            const entityMap = (0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, name);
            if (entityMap) {
                return (0, characterMap_1.getCharacterMapSlot)((0, characterMap_1.deserializeCharacterMap)(String(entityMap.variable.value ?? '{}')), player.characterName);
            }
            if (!variableStore) {
                console.warn(`getCharacterMapValue called for '${name}' but no VariableStore available`);
                return '';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            try {
                return variableStore.getCharacterMapValue(name, context);
            }
            catch (error) {
                console.error(`Failed to get character_map value '${name}':`, error);
                throw error;
            }
        },
        getCharacterMap: (name) => {
            const entityMap = (0, characterMap_1.getCurrentEntityCharacterMap)(entitiesService, player.id, name);
            if (entityMap) {
                return (0, characterMap_1.deserializeCharacterMap)(String(entityMap.variable.value ?? '{}'));
            }
            if (!variableStore) {
                console.warn(`getCharacterMap called for '${name}' but no VariableStore available`);
                return {};
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            try {
                return variableStore.getCharacterMap(name, context);
            }
            catch (error) {
                console.error(`Failed to get character_map '${name}':`, error);
                throw error;
            }
        },
        isOpeningSessionStackTopValid: () => Boolean(player.openingSessionTopValid),
        getOpeningSessionId: () => {
            if (!variableStore) {
                return '';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const parsed = (0, openingSessionStack_1.parseOpeningSessionFrame)(variableStore.peekStack(openingSessionStack_1.OPENING_SESSION_STACK_VAR, context));
            return parsed?.sessionId ? String(parsed.sessionId).trim() : '';
        },
        getOpeningAdventureId: () => {
            if (!variableStore) {
                return '';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const parsed = (0, openingSessionStack_1.parseOpeningSessionFrame)(variableStore.peekStack(openingSessionStack_1.OPENING_SESSION_STACK_VAR, context));
            return parsed?.triggeringAdventureId ? String(parsed.triggeringAdventureId).trim() : '';
        },
        openingAdventureHasTag: (tag) => {
            const wanted = String(tag || '').trim();
            if (!wanted || !variableStore || !adventureManager) {
                return false;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const parsed = (0, openingSessionStack_1.parseOpeningSessionFrame)(variableStore.peekStack(openingSessionStack_1.OPENING_SESSION_STACK_VAR, context));
            const adventureId = parsed?.triggeringAdventureId ? String(parsed.triggeringAdventureId).trim() : '';
            if (!adventureId) {
                return false;
            }
            const tags = adventureManager.getAdventure(adventureId)?.getHeader()?.tags ?? [];
            return tags.includes(wanted);
        },
        isOpeningSessionValid: (frame) => {
            if (!frame || typeof frame !== 'string')
                return false;
            if (!(0, openingSessionStack_1.parseOpeningSessionFrame)(frame))
                return false;
            if (!variableStore)
                return false;
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const top = variableStore.peekStack(openingSessionStack_1.OPENING_SESSION_STACK_VAR, context);
            if (!top || top !== frame)
                return false;
            return Boolean(player.openingSessionTopValid);
        },
        // Active adventure queries
        getActiveAdventureCountForUser: (adventureId) => {
            const details = player.activeAdventureDetails?.[adventureId];
            return details?.count ?? 0;
        },
        getUserActiveAdventureJoinTarget: (adventureId) => {
            const details = player.activeAdventureDetails?.[adventureId];
            if (!details || details.count !== 1 || !details.sessionId || !details.playerSlot) {
                return undefined;
            }
            if (details.joinable === false) {
                return undefined;
            }
            return `${details.sessionId}:${details.playerSlot}`;
        },
        isUserActiveAdventureJoinable: (adventureId) => {
            const details = player.activeAdventureDetails?.[adventureId];
            if (!details || details.count !== 1) {
                return false;
            }
            return details.joinable !== false;
        },
        listAdventureIdsByTag: (tag) => {
            if (!tag || typeof tag !== 'string' || !adventureManager) {
                return [];
            }
            return adventureManager.listAdventureIdsByTag(tag);
        },
        hasActiveAdventuresByTags: (tagsAttr) => {
            const filterTags = (0, adventureTags_1.parseAdventureListFilterTags)(tagsAttr);
            if (filterTags.length === 0) {
                return Boolean(player.hasActiveAdventures);
            }
            if (!adventureManager) {
                return false;
            }
            const taggedIds = new Set();
            for (const tag of filterTags) {
                for (const id of adventureManager.listAdventureIdsByTag(tag)) {
                    taggedIds.add(id);
                }
            }
            for (const adventureId of taggedIds) {
                if ((player.activeAdventureDetails?.[adventureId]?.count ?? 0) > 0) {
                    return true;
                }
            }
            return false;
        },
        getJoinAdventureTargetByTag: (tag) => {
            if (!tag || typeof tag !== 'string' || !adventureManager) {
                return undefined;
            }
            const taggedIds = adventureManager.listAdventureIdsByTag(tag);
            const joinTargets = new Set();
            for (const adventureId of taggedIds) {
                const details = player.activeAdventureDetails?.[adventureId];
                if (!details || details.count !== 1 || !details.sessionId || !details.playerSlot) {
                    continue;
                }
                if (details.joinable === false) {
                    continue;
                }
                joinTargets.add(`${details.sessionId}:${details.playerSlot}`);
            }
            if (joinTargets.size !== 1) {
                return undefined;
            }
            const [target] = Array.from(joinTargets);
            return `JOIN_ADVENTURE:${target}`;
        },
        /**
         * Adventure scene existence check for debug/UI options.
         * Useful for "optional" navigation targets that may not exist in all adventures.
         */
        adventureHasScene: (sceneId) => {
            if (!sceneId || typeof sceneId !== 'string')
                return false;
            if (!adventureManager)
                return false;
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure)
                return false;
            return !!adventure.getScene(sceneId);
        },
        // Session and player helpers
        getUserId: () => player.userId || 'anonymous',
        getCurrentSessionId: () => gameSession.id,
        getCurrentAdventureId: () => gameSession.adventureId,
        getPlayerCount: () => gameSession.players.length,
        getPlayerId: () => player.id,
        getPlayerSlot: () => player.playerName ?? player.id,
        getCharacterPortraitsInScene: (sceneId) => {
            if (!sceneId || typeof sceneId !== 'string') {
                return '';
            }
            const playersInScene = gameSession.players.filter(p => p.currentScene === sceneId);
            if (playersInScene.length === 0) {
                return '';
            }
            if (!adventureManager) {
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                return '';
            }
            const sorted = [...playersInScene].sort((a, b) => {
                const order = (name) => (name === 'host' ? 0 : parseInt(name.replace('player', ''), 10) || 99);
                return order(a.playerName ?? '') - order(b.playerName ?? '');
            });
            const tags = sorted.map(p => `<portrait:${resolvePortraitForCharacterName(p.characterName) || 'generic'}:thumbnail>`);
            return tags.join('\n\n');
        },
        /**
         * Portraits for characters whose `character_location` matches `locationTag`.
         * @param excludeViewerCharacterName When set (e.g. viewing player's character), that character is omitted — used so Location-With-Portraits does not duplicate the current player's portrait.
         */
        getCharacterPortraitsAtLocation: (locationTag, excludeViewerCharacterName) => {
            if (!locationTag || typeof locationTag !== 'string') {
                return '';
            }
            if (!variableStore) {
                return '';
            }
            const getCharacterLocation = (characterName, p) => {
                const context = {
                    playerId: p?.id || player.id,
                    characterName,
                    currentSceneId: p?.currentScene || player.currentScene,
                    userId: p?.userId || player.userId || 'anonymous'
                };
                const value = variableStore.getVariable('character_location', context);
                return typeof value === 'string' ? value : '';
            };
            if (!adventureManager) {
                return '';
            }
            const adventure = adventureManager.getAdventure(gameSession.adventureId);
            if (!adventure) {
                return '';
            }
            const characterNames = new Set();
            // Include session players at this location.
            for (const p of gameSession.players) {
                if (excludeViewerCharacterName && p.characterName === excludeViewerCharacterName) {
                    continue;
                }
                if (getCharacterLocation(p.characterName, p) === locationTag) {
                    characterNames.add(p.characterName);
                }
            }
            // Include active NPC definitions at this location (NPCs are not necessarily mapped to a Player).
            for (const npc of adventure.getNpcDefinitions()) {
                if (getCharacterLocation(npc.name) === locationTag) {
                    characterNames.add(npc.name);
                }
            }
            if (characterNames.size === 0) {
                return '';
            }
            const tags = [...characterNames]
                .sort((a, b) => a.localeCompare(b))
                .map(name => `<portrait:${resolvePortraitForCharacterName(name) || 'generic'}:thumbnail>`);
            return tags.join('\n\n');
        },
        getCharacterPortraitsForList: (characterNames) => {
            if (!Array.isArray(characterNames) || characterNames.length === 0) {
                return '';
            }
            const tags = characterNames
                .map((name) => resolvePortraitForCharacterName(String(name)))
                .map((portraitId) => `<portrait:${portraitId || 'generic'}:thumbnail>`);
            return tags.join('\n\n');
        },
        getLocationImageTag: (locationTag) => {
            if (!locationTag || typeof locationTag !== 'string') {
                return '<location-image:generic_location>';
            }
            const safeTag = locationTag.trim();
            if (!safeTag) {
                return '<location-image:generic_location>';
            }
            return `<location-image:${gameSession.adventureId}:${safeTag}>`;
        },
        generateCharacterComments: (variableValue) => {
            if (variableValue === undefined || variableValue === null || variableValue === '') {
                return '';
            }
            let entries;
            try {
                const parsed = typeof variableValue === 'string' ? JSON.parse(variableValue) : variableValue;
                entries = Array.isArray(parsed) ? parsed : [];
            }
            catch {
                return '';
            }
            if (entries.length === 0) {
                return '';
            }
            const sorted = [...entries].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            const getPortrait = (entry) => {
                const characterName = entry.characterName || 'Unknown';
                if (!adventureManager)
                    return 'generic';
                const adventure = adventureManager.getAdventure(gameSession.adventureId);
                if (!adventure)
                    return 'generic';
                const character = adventure.getCharacter(characterName);
                if (!character)
                    return 'generic';
                // Use stored playerId (author at post time) for portrait resolution when portrait uses ${user_portrait_id}
                let targetPlayer = entry.playerId
                    ? gameSession.getPlayer(entry.playerId)
                    : undefined;
                if (!targetPlayer) {
                    targetPlayer = player.characterName === characterName
                        ? player
                        : gameSession.getPlayerByCharacter(characterName) ?? player;
                }
                return resolvePortraitId(character.portraitId, characterName, targetPlayer, variableStore, gameSession);
            };
            const escapeAttr = (s) => String(s)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            return sorted
                .map((e) => `<CharacterComment character="${escapeAttr(e.characterName || 'Unknown')}" portrait="${escapeAttr(getPortrait(e))}" text="${escapeAttr(e.text || '')}" timestamp="${e.timestamp || 0}">`)
                .join('\n');
        },
        getUpcomingEventsForCharacter: (scheduleVar, characterName) => {
            const { events } = getScheduleData(scheduleVar);
            const viewer = characterName && typeof characterName === 'string' ? characterName : player.characterName;
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            return events
                .filter((event) => {
                if (!event)
                    return false;
                if (Array.isArray(event.requiredCharacters)) {
                    return event.requiredCharacters.includes(viewer);
                }
                return true;
            })
                .filter((event) => {
                const start = Number(event.scheduledTimeEpochSeconds);
                if (!Number.isFinite(start) || start <= 0) {
                    return true;
                }
                const duration = Number(event.durationMinutes);
                const end = Number.isFinite(duration) ? start + duration * 60 : start;
                return end >= nowSeconds;
            })
                .sort((a, b) => {
                const aTime = Number(a.scheduledTimeEpochSeconds) || 0;
                const bTime = Number(b.scheduledTimeEpochSeconds) || 0;
                return aTime - bTime;
            });
        },
        /** Returns true when the schedule contains an event with the given title (exact match after trim). */
        hasScheduleEventWithTitle: (scheduleVar, title) => {
            if (!scheduleVar || typeof scheduleVar !== 'string')
                return false;
            const searchTitle = (title ?? '').toString().trim();
            if (!searchTitle)
                return false;
            const { events } = getScheduleData(scheduleVar);
            return events.some((event) => event && String(event.title).trim() === searchTitle);
        },
        /** Returns true when generateScheduleBoard would show at least one event */
        hasUpcomingScheduleEvents: (scheduleVar, characterName) => {
            const events = getScheduleData(scheduleVar).events;
            const viewer = characterName && typeof characterName === 'string' ? characterName : player.characterName;
            const count = events.filter((event) => {
                if (!event)
                    return false;
                if (event.state === 'in-progress')
                    return false;
                if (Array.isArray(event.requiredCharacters)) {
                    return event.requiredCharacters.includes(viewer);
                }
                return true;
            }).length;
            return count > 0;
        },
        hasUpcomingEventsAt: (scheduleVar, location, characterName) => {
            if (!scheduleVar || !location || typeof location !== 'string')
                return false;
            const { events } = getScheduleData(scheduleVar);
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            const character = characterName && typeof characterName === 'string' ? characterName : undefined;
            return events.some((event) => {
                if (!event || event.location !== location)
                    return false;
                if (character && Array.isArray(event.requiredCharacters) && !event.requiredCharacters.includes(character))
                    return false;
                return (0, scheduleEventHelpers_1.isScheduleEventLocationOpen)(event, nowSeconds);
            });
        },
        getUpcomingEventsAt: (scheduleVar, location, characterName) => {
            if (!scheduleVar || !location || typeof location !== 'string')
                return '';
            const { events } = getScheduleData(scheduleVar);
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            const character = characterName && typeof characterName === 'string' ? characterName : undefined;
            const escapeAttr = (value) => String(value)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            const upcoming = events
                .filter((event) => {
                if (!event || event.location !== location)
                    return false;
                if (character && Array.isArray(event.requiredCharacters) && !event.requiredCharacters.includes(character))
                    return false;
                return (0, scheduleEventHelpers_1.isScheduleEventLocationOpen)(event, nowSeconds);
            })
                .sort((a, b) => Number(a.scheduledTimeEpochSeconds) - Number(b.scheduledTimeEpochSeconds));
            if (upcoming.length === 0)
                return '';
            return upcoming
                .map((event) => {
                const id = escapeAttr(event.id || '');
                const title = escapeAttr(event.title || 'Untitled event');
                const start = String(Number(event.scheduledTimeEpochSeconds));
                const startScene = escapeAttr(event.startScene || 'event_start');
                return `<JoinEventCard id="${id}" title="${title}" start="${start}" startScene="${startScene}">`;
            })
                .join('\n');
        },
        generateScheduleBoard: (scheduleVar, characterName, selectionTarget) => {
            const events = getScheduleData(scheduleVar).events;
            const viewer = characterName && typeof characterName === 'string' ? characterName : player.characterName;
            const filtered = events
                .filter((event) => {
                if (!event)
                    return false;
                if (event.state === 'in-progress')
                    return false;
                if (Array.isArray(event.requiredCharacters)) {
                    return event.requiredCharacters.includes(viewer);
                }
                return true;
            })
                .sort((a, b) => {
                const aTime = Number(a.scheduledTimeEpochSeconds) || 0;
                const bTime = Number(b.scheduledTimeEpochSeconds) || 0;
                return aTime - bTime;
            });
            if (!filtered || filtered.length === 0) {
                return '';
            }
            const escapeAttr = (value) => String(value)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            const formatEvent = (event) => {
                const title = escapeAttr(event.title || 'Untitled event');
                const location = escapeAttr(event.location || 'Unknown location');
                const duration = Number(event.durationMinutes);
                const durationText = Number.isFinite(duration) && duration > 0 ? String(duration) : '';
                const scheduledStart = Number(event.scheduledTimeEpochSeconds);
                let displayStart = Number.isFinite(scheduledStart) && scheduledStart > 0 ? scheduledStart : 0;
                let displayRealWorldLoc = typeof event.realWorldLocation === 'string' ? event.realWorldLocation.trim() : '';
                if (displayStart <= 0 && event.state !== 'scheduled') {
                    const proposedTimes = Array.isArray(event.proposedTimes) ? event.proposedTimes : [];
                    if (proposedTimes.length > 0) {
                        const latest = proposedTimes[proposedTimes.length - 1];
                        const proposedStart = Number(latest?.startTimeEpochSeconds);
                        if (Number.isFinite(proposedStart) && proposedStart > 0) {
                            displayStart = proposedStart;
                            const proposedLoc = typeof latest?.realWorldLocation === 'string' ? latest.realWorldLocation.trim() : '';
                            if (proposedLoc) {
                                displayRealWorldLoc = proposedLoc;
                            }
                        }
                    }
                }
                const startText = displayStart > 0 ? String(displayStart) : '';
                const required = Array.isArray(event.requiredCharacters)
                    ? escapeAttr(event.requiredCharacters.join(','))
                    : '';
                const charToPortrait = (name) => resolvePortraitForCharacterName(name);
                const requiredPortraits = Array.isArray(event.requiredCharacters)
                    ? escapeAttr(event.requiredCharacters.map(charToPortrait).join(','))
                    : '';
                const state = escapeAttr(event.state || 'proposed');
                // Portraits of characters who have not proposed or accepted any time (show in grey)
                let unconfirmedPortraits = '';
                if (event.state !== 'scheduled') {
                    const proposedTimes = Array.isArray(event.proposedTimes) ? event.proposedTimes : [];
                    const charsWithAction = new Set();
                    for (const p of proposedTimes) {
                        if (p?.proposedBy)
                            charsWithAction.add(String(p.proposedBy));
                        if (Array.isArray(p?.acceptedBy)) {
                            for (const c of p.acceptedBy)
                                charsWithAction.add(String(c));
                        }
                    }
                    const unconfirmedChars = Array.isArray(event.requiredCharacters)
                        ? event.requiredCharacters.filter((c) => !charsWithAction.has(c))
                        : [];
                    const unconfirmedPortraitIds = unconfirmedChars.map(charToPortrait).filter(Boolean);
                    if (unconfirmedPortraitIds.length > 0) {
                        unconfirmedPortraits = escapeAttr(unconfirmedPortraitIds.join(','));
                    }
                }
                const id = escapeAttr(event.id || '');
                const locationImageId = event.location
                    ? escapeAttr(`${gameSession.adventureId}:${String(event.location)}`)
                    : 'generic_location';
                const targetAttr = selectionTarget ? ` selectionTarget="${escapeAttr(selectionTarget)}"` : '';
                // Instruction text: show below time/duration in italics (only when time not confirmed)
                let instruction = '';
                if (event.state !== 'scheduled') {
                    const proposedTimes = Array.isArray(event.proposedTimes) ? event.proposedTimes : [];
                    if (proposedTimes.length === 0) {
                        instruction = 'Somebody needs to propose a time.';
                    }
                    else {
                        const hasProposed = proposedTimes.some((p) => p?.proposedBy === viewer);
                        const hasAccepted = proposedTimes.some((p) => Array.isArray(p?.acceptedBy) && p.acceptedBy.includes(viewer));
                        if (!hasProposed && !hasAccepted) {
                            instruction = 'You need to propose or accept a time.';
                        }
                        else {
                            instruction = 'Waiting for the others to confirm.';
                        }
                    }
                }
                const instructionAttr = instruction ? ` instruction="${escapeAttr(instruction)}"` : '';
                const unconfirmedAttr = unconfirmedPortraits ? ` unconfirmedPortraits="${unconfirmedPortraits}"` : '';
                const realWorldLocationAttr = displayRealWorldLoc.length > 0 ? ` realWorldLocation="${escapeAttr(displayRealWorldLoc)}"` : '';
                return `<ScheduleEvent id="${id}" title="${title}" location="${location}" locationImage="${locationImageId}" start="${startText}" duration="${durationText}" required="${required}" portraits="${requiredPortraits}" state="${state}"${targetAttr}${instructionAttr}${unconfirmedAttr}${realWorldLocationAttr}>`;
            };
            return filtered.map(formatEvent).join('\n');
        },
        // Schedule board helpers - simplify adventure scripting with clear, reusable functions
        getSelectedScheduleEventId: (eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            return resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
        },
        getScheduleEventTitle: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', fallback = 'Event Planning') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return fallback;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return fallback;
            return event.title || 'Untitled event';
        },
        getScheduleEventLocationImageTag: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return '';
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return '';
            const loc = event.location || '';
            const safeTag = (typeof loc === 'string' ? loc.trim() : '') || 'generic_location';
            return `<location-image:${gameSession.adventureId}:${safeTag}>`;
        },
        /** True when the selected event's game location is open (scheduled start reached or event in-progress). */
        isScheduleEventLocationOpen: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return false;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return false;
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            return (0, scheduleEventHelpers_1.isScheduleEventLocationOpen)(event, nowSeconds);
        },
        /** Markup guiding players to the in-game and real-world locations when the event location is open. */
        getScheduleEventLocationGuidanceMarkup: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return '';
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return '';
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            if (!(0, scheduleEventHelpers_1.isScheduleEventLocationOpen)(event, nowSeconds))
                return '';
            const loc = event.location || '';
            const safeTag = (typeof loc === 'string' ? loc.trim() : '') || 'generic_location';
            const imageTag = `<location-image:${gameSession.adventureId}:${safeTag}>`;
            let markup = `In the game you need to find this location:\n\n${imageTag}`;
            const realWorldLoc = typeof event.realWorldLocation === 'string' ? event.realWorldLocation.trim() : '';
            if (realWorldLoc) {
                markup += `\n\nIn the real world you will need to be at this location:\n\n*${realWorldLoc}*`;
            }
            return markup;
        },
        getScheduleEventRequiredPortraits: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return '';
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event || !Array.isArray(event.requiredCharacters))
                return '';
            const names = event.requiredCharacters;
            return names
                .map((n) => resolvePortraitForCharacterName(String(n)))
                .map((p) => `<portrait:${p || 'generic'}:thumbnail>`)
                .join('\n\n');
        },
        getScheduleEventRequiredPortraitsWithPresence: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return '';
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event || !Array.isArray(event.requiredCharacters))
                return '';
            const names = event.requiredCharacters;
            const eventStartScene = typeof event.startScene === 'string' ? event.startScene.trim() : '';
            const escapeAttr = (value) => String(value)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            const allPortraits = names.map((n) => resolvePortraitForCharacterName(String(n)));
            const absentPortraits = names
                .filter((n) => {
                if (!eventStartScene)
                    return false;
                const charPlayer = gameSession.getPlayerByCharacter(n);
                return !charPlayer || charPlayer.currentScene !== eventStartScene;
            })
                .map((n) => resolvePortraitForCharacterName(String(n)));
            const portraitsAttr = escapeAttr(allPortraits.join(','));
            const absentAttr = absentPortraits.length > 0 ? ` absentPortraits="${escapeAttr(absentPortraits.join(','))}"` : '';
            return `<ScheduleEventAttendance portraits="${portraitsAttr}"${absentAttr}>`;
        },
        areAllEventCharactersPresent: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return false;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event || !Array.isArray(event.requiredCharacters) || event.requiredCharacters.length === 0)
                return false;
            if (event.state === 'in-progress')
                return true;
            const eventStartScene = typeof event.startScene === 'string' ? event.startScene.trim() : '';
            if (!eventStartScene)
                return false;
            return event.requiredCharacters.every((name) => {
                const charPlayer = gameSession.getPlayerByCharacter(String(name));
                return !!charPlayer && charPlayer.currentScene === eventStartScene;
            });
        },
        getScheduleEventDurationText: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', fallback = '—') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return fallback;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return fallback;
            const duration = Number(event.durationMinutes);
            return Number.isFinite(duration) && duration > 0 ? `${duration} min` : fallback;
        },
        hasScheduleEventConfirmedTime: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return false;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return false;
            const t = Number(event.scheduledTimeEpochSeconds);
            return Number.isFinite(t) && t > 0;
        },
        getScheduleEventConfirmedTimeFriendly: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', fallback = 'TBD') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return fallback;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return fallback;
            const t = Number(event.scheduledTimeEpochSeconds);
            if (!Number.isFinite(t) || t <= 0)
                return fallback;
            return formatFriendlyDateTimeForPlayer(t);
        },
        getScheduleEventConfirmedRealWorldLocation: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', fallback = '') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return fallback;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return fallback;
            const loc = typeof event.realWorldLocation === 'string' ? event.realWorldLocation.trim() : '';
            return loc || fallback;
        },
        /** Italic "At:" line for confirmed real-world location; empty when location guidance is shown instead. */
        getScheduleEventConfirmedRealWorldLocationLine: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', fallback = '') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return '';
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return '';
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            if ((0, scheduleEventHelpers_1.isScheduleEventLocationOpen)(event, nowSeconds))
                return '';
            const loc = typeof event.realWorldLocation === 'string' ? event.realWorldLocation.trim() : '';
            return `*At: ${loc || fallback}*`;
        },
        hasScheduleEventRequiresRealWorldLocation: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return false;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            return event?.requiresRealWorldLocation === true;
        },
        getProposedRealWorldLocation: (locationVar = 'schedule_proposed_real_world_location', fallback = '') => {
            const raw = getVar(locationVar);
            if (raw === undefined || raw === null)
                return fallback;
            const loc = String(raw).trim();
            return loc || fallback;
        },
        getScheduleEventTimeUntilFriendly: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', fallback = '') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return fallback;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return fallback;
            const t = Number(event.scheduledTimeEpochSeconds);
            if (!Number.isFinite(t) || t <= 0)
                return fallback;
            return (0, dateTime_1.formatTimeUntil)(t, resolveNowMs());
        },
        /** Format epoch seconds as a friendly time-until string (e.g. "Starts in 2 days"). */
        formatTimeUntilFriendly: (epochSeconds) => (0, dateTime_1.formatTimeUntil)(epochSeconds, resolveNowMs()),
        /** Current game time in UTC milliseconds (respects playthrough/test clock overrides). */
        getNowMs: () => resolveNowMs(),
        getScheduleEventTimeOptionsMarkup: (scheduleVar, selectionTarget, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            return doGenerateScheduleEventTimeOptions(scheduleVar, String(eventId || ''), selectionTarget);
        },
        hasScheduleEventNeedsTimeProposal: (scheduleVar, eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            if (!eventId || String(eventId).trim() === '')
                return false;
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return false;
            const t = Number(event.scheduledTimeEpochSeconds);
            return !(Number.isFinite(t) && t > 0);
        },
        hasScheduleEventSelected: (eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id') => {
            const id = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            return id !== '';
        },
        getProposedTimeFriendly: (proposedTimeVar = 'schedule_proposed_time', fallback = 'No time selected.') => {
            const t = getVar(proposedTimeVar);
            if (!t)
                return fallback;
            const epoch = Number(t);
            if (!Number.isFinite(epoch) || epoch <= 0)
                return fallback;
            return formatFriendlyDateTimeForPlayer(epoch);
        },
        /** Returns approximate "X hours from now" or "in about X minutes" for HH:MM variable. If time is past today, treats as next day. Empty if invalid. */
        getTimeUntilTodayHHMM: (variableName, fallback = '') => {
            const raw = getVar(variableName);
            const hhmm = (raw ?? '').toString().trim();
            if (!/^\d{1,2}:\d{2}$/.test(hhmm))
                return fallback;
            const offset = getTimezoneOffsetMinutes();
            const nowMs = resolveNowMs();
            const targetUtcMs = (0, dateTime_1.localHHMMToNextUtcMs)(hhmm, offset, nowMs);
            if (targetUtcMs === null)
                return fallback;
            const diffMs = targetUtcMs - nowMs;
            const diffHours = diffMs / (1000 * 60 * 60);
            if (diffHours < 1) {
                const mins = Math.round(diffMs / (1000 * 60));
                return mins <= 1 ? 'in about a minute' : `in about ${mins} minutes`;
            }
            const hours = Math.round(diffHours * 2) / 2;
            return hours === 1 ? '~1 hour from now' : `~${hours} hours from now`;
        },
        /**
         * Returns a friendly approximation for a future date+time from YYYY-MM-DD and HH:MM variables.
         * Examples: "This coming Friday", "Next week Saturday night", "4 weeks from now".
         */
        getTimeUntilDateAndTimeFriendly: (dateVar = 'schedule_date_input', timeVar = 'schedule_time_input', fallback = '') => {
            const dateRaw = getVar(dateVar);
            const timeRaw = getVar(timeVar);
            const yyyymmdd = (dateRaw ?? '').toString().trim();
            const hhmm = (timeRaw ?? '').toString().trim();
            if (!/^\d{4}-\d{1,2}-\d{1,2}$/.test(yyyymmdd) || !/^\d{1,2}:\d{2}$/.test(hhmm))
                return fallback;
            const dp = yyyymmdd.split('-');
            const tp = hhmm.split(':');
            const year = parseInt(dp[0] ?? '0', 10);
            const month = parseInt(dp[1] ?? '1', 10) - 1;
            const day = parseInt(dp[2] ?? '1', 10);
            const hour = parseInt(tp[0] ?? '0', 10);
            const minute = parseInt(tp[1] ?? '0', 10) || 0;
            if (isNaN(year) || isNaN(month) || isNaN(day))
                return fallback;
            const target = new Date(year, month, day, hour, minute, 0, 0);
            const now = new Date();
            if (target.getTime() <= now.getTime())
                return fallback;
            const diffMs = target.getTime() - now.getTime();
            const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
            const weekday = target.toLocaleDateString(undefined, { weekday: 'long' });
            const timeOfDay = (h) => {
                if (h >= 21 || h < 5)
                    return 'night';
                if (h >= 17)
                    return 'evening';
                if (h >= 12)
                    return 'afternoon';
                return 'morning';
            };
            const tod = timeOfDay(hour);
            if (diffDays === 0) {
                const diffHours = diffMs / (1000 * 60 * 60);
                if (diffHours < 1) {
                    const mins = Math.round(diffMs / (1000 * 60));
                    return mins <= 1 ? 'in about a minute' : `in about ${mins} minutes`;
                }
                const hours = Math.round(diffHours * 2) / 2;
                return hours === 1 ? '~1 hour from now' : `~${hours} hours from now`;
            }
            if (diffDays === 1)
                return `${weekday} ${tod}`;
            if (diffDays >= 2 && diffDays <= 6)
                return `This coming ${weekday}`;
            if (diffDays >= 7 && diffDays <= 13)
                return `Next week ${weekday} ${tod}`;
            const weeks = Math.floor(diffDays / 7);
            if (weeks >= 2)
                return `${weeks} weeks from now`;
            return `This coming ${weekday}`;
        },
        hasEventAndProposedTime: (eventIdVar = 'schedule_event_id', userSelectedVar = 'user_selected_schedule_event_id', proposedTimeVar = 'schedule_proposed_time') => {
            const eventId = resolveSelectedScheduleEventId(eventIdVar, userSelectedVar);
            const proposedTime = getVar(proposedTimeVar);
            return (eventId !== undefined &&
                eventId !== null &&
                String(eventId).trim() !== '' &&
                proposedTime !== undefined &&
                proposedTime !== null &&
                String(proposedTime).trim() !== '');
        },
        generateScheduleEventTimeOptions: (scheduleVar, eventId, selectionTarget) => doGenerateScheduleEventTimeOptions(scheduleVar, eventId, selectionTarget),
        /** Current time in user's timezone, e.g. "7:52 PM" (for "Is currently ..." text). */
        getCurrentTimeFriendly: () => {
            const offset = getTimezoneOffsetMinutes();
            return (0, dateTime_1.formatTime12hFromOffset)(resolveNowMs(), offset);
        },
        /** Today's date in the player's local timezone as YYYY-MM-DD. */
        getLocalDateString: () => {
            const offset = getTimezoneOffsetMinutes();
            return (0, dateTime_1.getLocalDateStringFromOffset)(offset);
        },
        /** Hours and minutes until local midnight, e.g. "5 hours and 12 minutes". */
        getTimeUntilLocalMidnightFriendly: () => {
            const offset = getTimezoneOffsetMinutes();
            return (0, dateTime_1.formatHoursAndMinutesUntilLocalMidnight)(offset);
        },
        /** Next 15-minute slot label in user's timezone (matches proposeScheduleTimeNext15Min). */
        getScheduleTimeNext15MinLabel: () => {
            const offset = getTimezoneOffsetMinutes();
            const next = (0, dateTime_1.nextLocalSlotUtcMs)(offset, resolveNowMs(), 5, 15);
            return (0, dateTime_1.formatTime12hFromOffset)(next, offset);
        },
        /** Next 30-minute slot label in user's timezone (matches proposeScheduleTimeNext30Min). */
        getScheduleTimeNext30MinLabel: () => {
            const offset = getTimezoneOffsetMinutes();
            const next = (0, dateTime_1.nextLocalSlotUtcMs)(offset, resolveNowMs(), 20, 15);
            return (0, dateTime_1.formatTime12hFromOffset)(next, offset);
        },
        isEventActive: (scheduleVar, eventId) => {
            if (!scheduleVar || !eventId) {
                return false;
            }
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event)
                return false;
            const start = Number(event.scheduledTimeEpochSeconds);
            if (!Number.isFinite(start) || start <= 0)
                return false;
            const duration = Number(event.durationMinutes);
            const durationSeconds = Number.isFinite(duration) && duration > 0 ? duration * 60 : 0;
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            return nowSeconds >= start && nowSeconds <= start + durationSeconds;
        },
        areRequiredCharactersAtLocation: (scheduleVar, eventId) => {
            if (!scheduleVar || !eventId) {
                return false;
            }
            if (!variableStore) {
                return false;
            }
            const { events } = getScheduleData(scheduleVar);
            const event = events.find((e) => e?.id === eventId);
            if (!event || !event.location || !Array.isArray(event.requiredCharacters)) {
                return false;
            }
            const targetLocation = String(event.location);
            return event.requiredCharacters.every((characterName) => {
                const requiredPlayer = gameSession.getPlayerByCharacter(characterName);
                if (!requiredPlayer)
                    return false;
                const requiredContext = VariableStore_1.VariableStore.buildVariableContext(requiredPlayer.id, requiredPlayer.characterName, requiredPlayer.currentScene, requiredPlayer.userId || 'anonymous');
                const locationValue = variableStore.getVariable('character_location', requiredContext);
                return typeof locationValue === 'string' && locationValue === targetLocation;
            });
        },
        getCharacterSynchronisationWaitOption: (text, syncVar, targetScene, characterNames, targetByCharacter, syncEffects) => {
            const escapeAttr = (value) => String(value)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            if (!syncVar || typeof syncVar !== 'string' || syncVar.trim() === '')
                return '';
            const names = Array.isArray(characterNames)
                ? characterNames.map((n) => String(n).trim()).filter(Boolean)
                : typeof characterNames === 'string'
                    ? characterNames.split(',').map((n) => n.trim()).filter(Boolean)
                    : [];
            if (names.length === 0)
                return '';
            const hasTargetByCharacter = targetByCharacter &&
                typeof targetByCharacter === 'object' &&
                Object.keys(targetByCharacter).length > 0;
            const fallbackTarget = typeof targetScene === 'string' && targetScene.trim() !== ''
                ? targetScene.trim()
                : hasTargetByCharacter && names[0]
                    ? (targetByCharacter[names[0]] ?? '')
                    : '';
            if (!fallbackTarget && !hasTargetByCharacter)
                return '';
            const raw = getVar(syncVar);
            let clicked = [];
            if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
                try {
                    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    clicked = Array.isArray(parsed) ? parsed.map((c) => String(c).trim()) : [];
                }
                catch {
                    clicked = [];
                }
            }
            const allPortraits = names.map((n) => resolvePortraitForCharacterName(n));
            const unclickedPortraits = names
                .filter((n) => !clicked.includes(n))
                .map((n) => resolvePortraitForCharacterName(n));
            const textAttr = escapeAttr(String(text ?? '').trim() || 'Ready');
            const syncVarAttr = escapeAttr(syncVar.trim());
            const targetAttr = escapeAttr(fallbackTarget);
            const charactersAttr = escapeAttr(names.join(','));
            const portraitsAttr = escapeAttr(allPortraits.join(','));
            const unclickedAttr = unclickedPortraits.length > 0 ? ` unclickedPortraits="${escapeAttr(unclickedPortraits.join(','))}"` : '';
            const targetByCharacterAttr = hasTargetByCharacter
                ? ` targetByCharacter="${escapeAttr(names
                    .map((n) => `${n}:${(targetByCharacter[n] ?? '').trim()}`)
                    .filter((p) => p.length > 0 && p.includes(':'))
                    .join(','))}"`
                : '';
            const syncEffectsAttr = typeof syncEffects === 'string' && syncEffects.trim() !== ''
                ? ` syncEffects="${escapeAttr(syncEffects.trim())}"`
                : '';
            return `<SyncOption text="${textAttr}" syncVar="${syncVarAttr}" target="${targetAttr}" characters="${charactersAttr}" portraits="${portraitsAttr}"${unclickedAttr}${targetByCharacterAttr}${syncEffectsAttr}>`;
        },
        getSessionPlayersDebugInfo: () => {
            const sorted = [...gameSession.players].sort((a, b) => {
                const order = (name) => (name === 'host' ? 0 : parseInt(name.replace('player', ''), 10) || 99);
                return order(a.playerName ?? '') - order(b.playerName ?? '');
            });
            return sorted
                .map((p) => {
                const slot = p.playerName ?? p.id;
                const uid = p.userId != null ? String(p.userId) : '—';
                const suffix = p.id === player.id ? ' (you)' : '';
                return `**${slot}: ** ${uid} - ${p.id}${suffix}`;
            })
                .join('\n\n');
        },
        getVariablesDebugInfo: () => {
            if (!variableStore) {
                return 'VariableStore not available.';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const returnStack = variableStore.getStackVariable('return_scene', context) ?? [];
            const additionalSceneIds = [];
            for (const sceneId of returnStack) {
                const trimmed = String(sceneId || '').trim();
                if (trimmed && trimmed !== context.currentSceneId && !additionalSceneIds.includes(trimmed)) {
                    additionalSceneIds.push(trimmed);
                }
            }
            return variableStore.getVariablesDebugInfo(context, { additionalSceneIds });
        },
        isSessionHost: () => {
            const createdBy = gameSession.createdByUserId;
            return player.userId === createdBy;
        },
        isPlayerSlotEmpty: (playerSlot) => {
            return !gameSession.getPlayerByPlayerName(playerSlot);
        },
        getCharacterTemplate: (characterName) => {
            const name = (characterName ?? player.currentCharacter ?? '').toString().trim();
            return (0, characterInstanceNames_1.getCharacterTemplateName)(name);
        },
        listPlayersForTemplate: (templateName) => {
            return (0, openCharacterRuntime_1.listPlayerNamesForTemplate)(gameSession, (templateName ?? '').toString());
        },
        getOpenGroupRemaining: () => {
            const adventure = adventureManager?.getAdventure(gameSession.adventureId);
            return (0, openCharacterRuntime_1.getOpenGroupRemaining)(gameSession, adventure?.players);
        },
        openGroupMinSatisfied: () => {
            const adventure = adventureManager?.getAdventure(gameSession.adventureId);
            return (0, openCharacterRuntime_1.openGroupMinSatisfied)(gameSession, adventure?.players);
        },
        getJoinedPlayerCount: () => {
            return gameSession.players.length;
        },
        /**
         * Players currently in this session with resolved display names
         * (`user_player_name` when available). Useful for pickers (e.g. track member).
         */
        listSessionPlayers: () => {
            const rows = [];
            for (const sessionPlayer of gameSession.players) {
                const userId = String(sessionPlayer.userId || '').trim();
                if (!userId || userId === 'anonymous') {
                    continue;
                }
                rows.push({
                    userId,
                    displayName: resolveSessionPlayerDisplayName(sessionPlayer),
                    playerId: String(sessionPlayer.id)
                });
            }
            return rows;
        },
        /**
         * Display names for all players currently in this session (user_player_name when available).
         * Useful for adventure-authored list labels (e.g. Active-Adventure-List labelVariable).
         */
        listSessionPlayerDisplayNames: () => {
            return gameSession.players
                .map((sessionPlayer) => resolveSessionPlayerDisplayName(sessionPlayer))
                .filter((name) => Boolean(name && name.trim()));
        },
        /**
         * Rich-text option lines for picking a session player by user id.
         * Each option sets `variableName` (default `selected_session_player_user_id`) and navigates to `listSceneId`.
         */
        generateSessionPlayerOptions: (listSceneId, variableName) => {
            const scene = (listSceneId ?? '').trim();
            if (!scene) {
                return '';
            }
            const varNameRaw = (variableName ?? 'selected_session_player_user_id').trim();
            const varName = varNameRaw.length > 0 ? varNameRaw : 'selected_session_player_user_id';
            const lines = [];
            for (const sessionPlayer of gameSession.players) {
                const userId = String(sessionPlayer.userId || '').trim();
                if (!userId || userId === 'anonymous') {
                    continue;
                }
                const effects = `setVariable("${escapeJsDoubleQuotedString(varName)}","${escapeJsDoubleQuotedString(userId)}")`;
                const label = escapeAttr(resolveSessionPlayerDisplayName(sessionPlayer));
                lines.push(`<option:target=${scene}:display=block:effects='''${effects}'''>${label}</option>`);
            }
            return lines.join('\n');
        },
        ...(0, hostScriptBagHelpers_1.buildHostScriptBagHelpers)({
            player,
            getCurrentEntityId: () => {
                if (!entitiesService) {
                    return '';
                }
                return entitiesService.getCurrentEntityId(player.id) ?? '';
            }
        }),
        /**
         * Next free player slot name (`host` / `player2` / …), including open-group seats within Max.
         * Returns empty string when the session is full.
         */
        getNextAvailablePlayerName: () => {
            const adventure = adventureManager?.getAdventure(gameSession.adventureId);
            if (!adventure?.players) {
                return '';
            }
            return (0, openCharacterRuntime_1.getNextAvailablePlayerSlotName)(gameSession, adventure.players) ?? '';
        },
        /**
         * Returns true if another player (not the current player) is currently using the given character.
         * Use this to hide character selection options when a character is already taken.
         */
        isCharacterUsedByAnotherPlayer: (characterName) => {
            return gameSession.players.some(p => p.id !== player.id && p.currentCharacter === characterName);
        },
        isGuestUser: () => {
            return gameSession.isGuest === true;
        },
        // Invite link generation
        generateInviteLink: (variablesObject) => {
            if (!inviteLinkService) {
                return '';
            }
            const baseUrl = inviteLinkService.getApplicationBaseUrl().replace(/\/+$/, '');
            if (!baseUrl) {
                return '';
            }
            const path = '/';
            if (!variablesObject || typeof variablesObject !== 'object' || Array.isArray(variablesObject)) {
                return baseUrl + path;
            }
            const params = Object.entries(variablesObject)
                .filter(([, v]) => v !== undefined && v !== null)
                .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
            if (params.length === 0) {
                return baseUrl + path;
            }
            return baseUrl + path + '?' + params.join('&');
        },
        // Adventure invite functions
        getAdventureInviteCount: () => {
            if (!adventureInviteService || !player.userId) {
                return 0;
            }
            return adventureInviteService.getInviteCount(player.userId);
        },
        getAdventureInvite: (inviteId) => {
            if (!adventureInviteService) {
                return undefined;
            }
            return adventureInviteService.getInvite(inviteId);
        },
        // Async operation status
        registrationProcessComplete: () => {
            if (!variableStore) {
                return false;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_registration_process_status', context);
            return status === 'success' || status === 'error' || status === 'access_denied';
        },
        validateEmailFormat: (email) => {
            if (!email || typeof email !== 'string' || email.trim().length === 0) {
                return false;
            }
            const trimmed = email.trim();
            const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
            return emailRegex.test(trimmed);
        },
        getRegistrationStatus: () => {
            if (!variableStore) {
                return 'none';
            }
            const userId = player.userId || 'anonymous';
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId
            };
            const status = variableStore.getVariable('user_registration_process_status', context);
            return status !== undefined && status !== null ? String(status) : 'none';
        },
        getRegistrationError: () => {
            if (!variableStore) {
                return undefined;
            }
            const userId = player.userId || 'anonymous';
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId
            };
            const error = variableStore.getVariable('user_registration_error', context);
            return error !== undefined && error !== null ? String(error) : undefined;
        },
        getSceneOpenTime: () => {
            if (!variableStore)
                return 0;
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const ts = variableStore.getVariable('user_scene_enter_timestamp', context);
            if (ts === undefined || ts === null)
                return 0;
            const timestamp = typeof ts === 'number' ? ts : Number(ts);
            if (Number.isNaN(timestamp) || timestamp <= 0)
                return 0;
            const nowSeconds = Math.floor(resolveNowMs() / 1000);
            return Math.max(0, nowSeconds - timestamp);
        },
        // Date/time helpers
        formatFriendlyDateTime: (epochSeconds) => formatFriendlyDateTimeForPlayer(epochSeconds),
        loginProcessComplete: () => {
            if (!variableStore) {
                return false;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_login_process_status', context);
            return status === 'success' || status === 'error' || status === 'access_denied';
        },
        getLoginStatus: () => {
            if (!variableStore) {
                return 'none';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_login_process_status', context);
            return status !== undefined && status !== null ? String(status) : 'none';
        },
        getLoginError: () => {
            if (!variableStore) {
                return undefined;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const error = variableStore.getVariable('user_login_error', context);
            return error !== undefined && error !== null ? String(error) : undefined;
        },
        emailCheckComplete: () => {
            if (!variableStore) {
                return false;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_email_check_status', context);
            return status === 'available' || status === 'unavailable' || status === 'error' || status === 'access_denied';
        },
        getEmailCheckStatus: () => {
            if (!variableStore) {
                return 'none';
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_email_check_status', context);
            return status !== undefined && status !== null ? String(status) : 'none';
        },
        getEmailCheckError: () => {
            if (!variableStore) {
                return undefined;
            }
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const error = variableStore.getVariable('user_email_check_error', context);
            return error !== undefined && error !== null ? String(error) : undefined;
        },
        // Email verification-code flows (send + verify)
        emailVerificationSendComplete: () => {
            if (!variableStore)
                return false;
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_email_verification_send_status', context);
            return status === 'success' || status === 'error';
        },
        getEmailVerificationSendStatus: () => {
            if (!variableStore)
                return 'none';
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_email_verification_send_status', context);
            return status !== undefined && status !== null ? String(status) : 'none';
        },
        getEmailVerificationSendError: () => {
            if (!variableStore)
                return undefined;
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const error = variableStore.getVariable('user_email_verification_send_error', context);
            return error !== undefined && error !== null ? String(error) : undefined;
        },
        emailVerificationVerifyComplete: () => {
            if (!variableStore)
                return false;
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_email_verification_verify_status', context);
            return status === 'success' || status === 'error';
        },
        getEmailVerificationVerifyStatus: () => {
            if (!variableStore)
                return 'none';
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const status = variableStore.getVariable('user_email_verification_verify_status', context);
            return status !== undefined && status !== null ? String(status) : 'none';
        },
        getEmailVerificationVerifyError: () => {
            if (!variableStore)
                return undefined;
            const context = {
                playerId: player.id,
                characterName: player.characterName,
                currentSceneId: player.currentScene,
                userId: player.userId || 'anonymous'
            };
            const error = variableStore.getVariable('user_email_verification_verify_error', context);
            return error !== undefined && error !== null ? String(error) : undefined;
        },
        // Analytics snapshot helpers (public names are centralized in `analyticsScriptSurface.ts`).
        getAnalyticsTopUsers: (limit) => {
            if (!analyticsService)
                return [];
            const lim = Math.max(1, Math.min(100, Math.floor(Number(limit)) || 10));
            return analyticsService.getAnalyticsTopUsers(lim);
        },
        getAnalyticsTotalInteractions: () => {
            if (!analyticsService)
                return 0;
            return analyticsService.getAnalyticsTotalInteractions();
        },
        getAnalyticsTopFeedbackUsers: (limit) => {
            if (!analyticsService)
                return [];
            const lim = Math.max(1, Math.min(100, Math.floor(Number(limit)) || 10));
            return analyticsService.getAnalyticsTopFeedbackUsers(lim);
        },
        getAnalyticsTotalFeedback: () => {
            if (!analyticsService)
                return 0;
            return analyticsService.getAnalyticsTotalFeedback();
        },
        getAnalyticsRecentFeedback: (limit) => {
            if (!analyticsService)
                return [];
            const lim = clampAnalyticsLimit(limit);
            return analyticsService.getAnalyticsRecentFeedback(lim);
        },
        getAnalyticsRecentGeneralFeedback: (limit) => {
            if (!analyticsService)
                return [];
            const lim = clampAnalyticsLimit(limit);
            return analyticsService.getAnalyticsRecentGeneralFeedback(lim);
        },
        getAnalyticsFeedbackAdventures: (limit) => {
            if (!analyticsService)
                return [];
            return analyticsService.getAnalyticsFeedbackAdventures(clampAnalyticsLimit(limit));
        },
        getAnalyticsRecentFeedbackForAdventure: (adventureId, limit) => {
            if (!analyticsService)
                return [];
            const id = (adventureId ?? '').toString().trim();
            if (!id)
                return [];
            return analyticsService.getAnalyticsRecentFeedbackForAdventure(id, clampAnalyticsLimit(limit));
        },
        generateAnalyticsFeedbackCards: (limit, selectionTarget) => {
            if (!analyticsService)
                return '';
            const lim = clampAnalyticsLimit(limit);
            const rows = analyticsService.getAnalyticsRecentFeedback(lim);
            if (!rows.length) {
                return '';
            }
            return rows.map((row) => feedbackRowToCardMarkup(row, selectionTarget)).join('\n');
        },
        /** Recent feedback cards for general (non–adventure-scoped) rows only. */
        generateAnalyticsFeedbackGeneralCards: (limit, selectionTarget) => {
            if (!analyticsService)
                return '';
            const lim = clampAnalyticsLimit(limit);
            const rows = analyticsService.getAnalyticsRecentGeneralFeedback(lim);
            if (!rows.length) {
                return '';
            }
            return rows.map((row) => feedbackRowToCardMarkup(row, selectionTarget, false)).join('\n');
        },
        generateAnalyticsFeedbackCardsForAdventure: (adventureId, limit, selectionTarget) => {
            if (!analyticsService)
                return '';
            const id = (adventureId ?? '').toString().trim();
            if (!id)
                return '';
            const lim = clampAnalyticsLimit(limit);
            const rows = analyticsService.getAnalyticsRecentFeedbackForAdventure(id, lim);
            if (!rows.length)
                return '';
            return rows.map((row) => feedbackRowToCardMarkup(row, selectionTarget, false)).join('\n');
        },
        /**
         * Rich-text fragment for an adventure title image, using the same storage id → markup rules as
         * `generateAnalyticsFeedbackAdventureOptions`. Pass the hub variable value (e.g. `feedback_hub_selected_adventure_id`).
         * Returns `<adventure:markupId>` when mappable, otherwise an HTML-escaped display name or id string.
         */
        getAnalyticsFeedbackAdventureTitleMarkup: (adventureStorageId) => {
            const raw = (adventureStorageId ?? '').toString().trim();
            if (!raw) {
                return '';
            }
            let resolvedForMarkup = raw;
            let fallbackDisplay;
            if (analyticsService) {
                const adventures = analyticsService.getAnalyticsFeedbackAdventures(100);
                const row = adventures.find((r) => {
                    const storage = (r.adventureId ?? '').trim() || (r.adventureName ?? '').trim();
                    return storage === raw;
                });
                if (row) {
                    resolvedForMarkup = (row.adventureId ?? '').trim() || (row.adventureName ?? '').trim() || raw;
                    fallbackDisplay = (row.adventureName ?? '').trim() || undefined;
                }
            }
            const markupId = toAdventureMarkupId(resolvedForMarkup);
            if (markupId !== 'generic') {
                return `<adventure:${markupId}>`;
            }
            return escapeAttr(fallbackDisplay || raw);
        },
        /**
         * Rich-text option lines: each option sets a string variable (default `feedback_hub_selected_adventure_id`)
         * and navigates to `listSceneId`. Label uses `<adventure:id>` title image when id parses cleanly, else plain text.
         */
        generateAnalyticsFeedbackAdventureOptions: (listSceneId, variableName, limit) => {
            if (!analyticsService) {
                return '';
            }
            const scene = (listSceneId ?? '').trim();
            if (!scene) {
                return '';
            }
            const varNameRaw = (variableName ?? 'feedback_hub_selected_adventure_id').trim();
            const varName = varNameRaw.length > 0 ? varNameRaw : 'feedback_hub_selected_adventure_id';
            const lim = clampAnalyticsLimit(limit);
            const adventures = analyticsService.getAnalyticsFeedbackAdventures(lim);
            if (!adventures.length) {
                return '';
            }
            const lines = [];
            for (const row of adventures) {
                const storageId = (row.adventureId ?? '').trim() || (row.adventureName ?? '').trim();
                if (!storageId)
                    continue;
                const effects = `setVariable("${escapeJsDoubleQuotedString(varName)}","${escapeJsDoubleQuotedString(storageId)}")`;
                const markupId = toAdventureMarkupId((row.adventureId ?? '').trim() || row.adventureName);
                const label = markupId !== 'generic'
                    ? `<adventure:${markupId}>`
                    : escapeAttr(row.adventureName || 'Adventure');
                lines.push(`<option:target=${scene}:display=block:effects='''${effects}'''>${label}</option>`);
            }
            return lines.join('\n');
        },
        ...(0, activityScriptHelpers_1.buildActivityScriptHelpers)(variableStore, player, gameSession, adventureManager, evaluateConditionScript, characterScriptAccess),
        ...(0, entityScriptHelpers_1.buildEntityScriptHelpers)(entitiesService, variableStore, player, gameSession, adventureManager),
        ...(0, foreignEntityScriptHelpers_1.buildForeignEntityScriptHelpers)(foreignEntityLookup, player),
        // Host-registered APIs (e.g. parseCsm from @collabajam/csm)
        ...(0, scriptApiExtensions_1.getScriptApiExtensions)()
    };
}
//# sourceMappingURL=ScriptContext.js.map